import { useEffect, useState } from 'react';
import { api } from './api';

// What the platform currently allows (GET /api/features). Cheers2Night is discovery-only: offer
// claiming and pool-party booking are switched off on the server, and alcohol-related content
// (alcohol and happy-hour offers, the wine-shop directory) shows only while the Super Admin allows it.
export interface PlatformFeatures {
  offerClaims: boolean;
  poolPartyBookings: boolean;
  alcoholOffers: boolean;
  // Partners may submit alcohol-related offers (each still needs admin approval).
  alcoholSubmissions: boolean;
  // Offer categories the Super Admin has switched off: hidden, can't be published.
  disabledCategories: string[];
}

// Everything off until the server says otherwise, so nothing restricted flashes on screen.
export const NO_FEATURES: PlatformFeatures = { offerClaims: false, poolPartyBookings: false, alcoholOffers: false, alcoholSubmissions: false, disabledCategories: [] };

let cached: Promise<PlatformFeatures> | null = null;

export function loadFeatures(): Promise<PlatformFeatures> {
  cached ??= api.get<PlatformFeatures>('/api/features').then((r) => ({ ...NO_FEATURES, ...r.data })).catch(() => {
    cached = null; // retry on the next page
    return NO_FEATURES;
  });
  return cached;
}

// After the admin changes the alcohol switches, so this tab shows the new state.
export function refreshFeatures() {
  cached = null;
}

export function useFeatures(): PlatformFeatures {
  const [features, setFeatures] = useState<PlatformFeatures>(NO_FEATURES);
  useEffect(() => {
    let alive = true;
    loadFeatures().then((f) => { if (alive) setFeatures(f); });
    return () => { alive = false; };
  }, []);
  return features;
}

// Offer categories (c2n.api OfferCategories), with the labels customers and partners see.
export const OFFER_CATEGORIES = [
  { value: 'Food', label: 'Food Discounts' },
  { value: 'Combo', label: 'Combos & Special Menus' },
  { value: 'NonAlcoholicDrinks', label: 'Non-Alcoholic Drinks' },
  { value: 'DiningBill', label: 'Dining Bill Discounts' },
  { value: 'LiveMusic', label: 'Live Music & Entertainment' },
  { value: 'Event', label: 'DJ Nights & Events' },
  { value: 'Other', label: 'Other Offers' },
  { value: 'HappyHour', label: 'Happy Hours' },
  { value: 'Alcohol', label: 'Alcohol Offers' }
] as const;

export type OfferCategory = (typeof OFFER_CATEGORIES)[number]['value'];

// Same rule as the API: happy hours, alcohol offers, and dining-bill discounts that cover alcohol (or
// don't say) follow the alcohol rules.
export const isAlcoholCategory = (category?: string | null, coversAlcohol?: boolean | null) =>
  category === 'HappyHour' || category === 'Alcohol' || (category === 'DiningBill' && coversAlcohol !== false);

export const DISCOUNT_TYPES = [
  { value: 'Percentage', label: 'Percentage off' },
  { value: 'FixedAmount', label: 'Fixed amount off (₹)' },
  { value: 'Other', label: 'Other (free item, combo price, ...)' }
] as const;

// "20% off", "₹200 off", or null for "Other" (the benefit text says what it is).
export const formatDiscount = (type?: string | null, value?: number | null) =>
  type === 'Percentage' && value ? `${Number(value)}% off`
    : type === 'FixedAmount' && value ? `₹${Number(value).toLocaleString('en-IN')} off`
      : null;

export const WEEK_DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;

// "Every day", "Weekends", "Mon, Wed, Fri".
export const formatDays = (days?: string[] | string | null) => {
  const list = (Array.isArray(days) ? days : (days ?? '').split(',')).map((d) => d.trim()).filter(Boolean);
  if (list.length === 0 || list.length === 7) return 'Every day';
  if (list.length === 2 && list.includes('Sat') && list.includes('Sun')) return 'Weekends';
  return list.join(', ');
};

export const categoryLabel = (category?: string | null) =>
  OFFER_CATEGORIES.find((c) => c.value === category)?.label ?? 'Uncategorised';
